/**
 * Failures the API client can raise, and the one place that turns any of them into copy a
 * person can act on. Kept free of imports so both `auth.ts` and `api.ts` can use it.
 *
 * Before D-055 every non-`ApiError` failure was reported as "The API could not be reached",
 * which covered three unrelated causes: an expired sign-in, a redirect in flight, and a real
 * transport failure. Each now has its own type and its own sentence.
 */

/** A sign-in redirect is in flight. Callers must not fall back to an anonymous request. */
export class AuthRedirectError extends Error {
  constructor(message = 'Your sign-in expired. Signing you in again.') {
    super(message);
    this.name = 'AuthRedirectError';
  }
}

/** Repeated redirects did not produce a token; stop rather than loop. */
export class AuthFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthFailedError';
  }
}

/** The request never reached the API: offline, DNS, TLS, or a response the browser blocked. */
export class NetworkError extends Error {
  constructor(public readonly reason: string) {
    super('The API could not be reached. Check your connection, then reload the page.');
    this.name = 'NetworkError';
  }
}

/**
 * A lazily loaded part of the app could not be fetched because the page was built before the
 * current deploy (D-059). Every deploy replaces the hashed asset files and the old ones stop
 * existing, so a tab left open asks for a chunk that is no longer there and gets a 404.
 */
export class StaleBuildError extends Error {
  constructor(public readonly reason: string) {
    super('A new version of Process Hub was published while this page was open. Reload to continue.');
    this.name = 'StaleBuildError';
  }
}

/**
 * Did a dynamic `import()` fail because the chunk is gone? Every browser words it differently and
 * none of them give a code, so the message is all there is to go on: Chrome "Failed to fetch
 * dynamically imported module", Firefox "error loading dynamically imported module", Safari
 * "Importing a module script failed".
 */
export function isChunkLoadFailure(e: unknown): boolean {
  const m = e instanceof Error ? e.message : String(e ?? '');
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch dynamically/i.test(m);
}

/** The shape of an `ApiError` without importing it, so a test double works the same way. */
function problemOf(e: unknown): { title: string; status: number; detail?: string } | null {
  if (typeof e !== 'object' || e === null) return null;
  const p = (e as { problem?: unknown }).problem;
  if (typeof p !== 'object' || p === null) return null;
  const { title, status, detail } = p as { title?: unknown; status?: unknown; detail?: unknown };
  if (typeof title !== 'string' || typeof status !== 'number') return null;
  return { title, status, detail: typeof detail === 'string' ? detail : undefined };
}

/**
 * One sentence for the person, whatever went wrong. `fallback` is the calling screen's own
 * words for a failure it cannot explain ("Publishing failed."); an expired sign-in or an
 * unreachable API always speaks for itself, because that is what the person has to act on.
 */
export function describeError(e: unknown, fallback = 'Something went wrong. Reload the page and try again.'): string {
  if (e instanceof AuthRedirectError || e instanceof AuthFailedError || e instanceof NetworkError || e instanceof StaleBuildError) return e.message;

  const problem = problemOf(e);
  if (problem) {
    if (problem.status === 401) return 'You are signed in, but the API did not accept the sign-in. Ask IT to confirm you have a Process Hub role.';
    if (problem.status === 403) return 'Your account is not allowed to use Process Hub.';
    if (problem.detail) return `${problem.title}: ${problem.detail}`;
    return `The API returned an error (${problem.status}). ${problem.title}`;
  }

  return fallback;
}
